import { StorageService } from '../../../core/services/storage.service';
import { DeviceType, OrderType, PaymentType } from '../constant/authentication.enum';

export interface LoginRequest {
    email: string;
    password: string;
    device: Device;
    reCaptchaToken: string;
}

export class DeviceModel {
    deviceNo: string;
    deviceType: DeviceType;
    registrationId: string;
    guiVersion: string;
    isIosSandBox: boolean;

    constructor(model: Device, storageService: StorageService) {
        this.deviceNo = storageService.deviceNo || this.generateUniqueDeviceNo();
        this.deviceType = model.deviceType || DeviceModel.detectDeviceType();
        this.registrationId = storageService.registrationId || this.generateUniqueRegistrationId();
        this.guiVersion = model.guiVersion || this.detectBrowserVersion();
        this.isIosSandBox = model.isIosSandBox || false;
    }

    private generateUniqueDeviceNo(): string {
        const array = new Uint32Array(4);

        crypto.getRandomValues(array);

        return (
            'WEB-' +
            Array.from(array, (dec) => dec.toString(16).padStart(8, '0'))
                .join('-')
                .toUpperCase()
        ).toString();
    }

    private generateUniqueRegistrationId(): string {
        const array = new Uint32Array(4);

        crypto.getRandomValues(array);

        return (
            'ID-' +
            Array.from(array, (dec) => dec.toString(16).padStart(8, '0'))
                .join('-')
                .toUpperCase()
        ).toString();
    }

    /**
     * Detects the current device type from the user agent. Static so it can be reused without
     * constructing a DeviceModel (e.g. by TrackingService) — keep this the single source of truth.
     */
    public static detectDeviceType(): DeviceType {
        const userAgent = navigator.userAgent || navigator.vendor;

        if (/mobile/i.test(userAgent)) {
            return DeviceType.MobileBrowser;
        } else {
            return DeviceType.Web;
        }
    }

    private detectBrowserVersion(): string {
        const userAgent = navigator.userAgent;
        let browserVersion = 'Unknown';

        if (userAgent.includes('Chrome')) {
            const version = userAgent.match(/Chrome\/([0-9.]+)/);
            browserVersion = version ? `Chrome ${version[1]}` : browserVersion;
        } else if (userAgent.includes('Firefox')) {
            const version = userAgent.match(/Firefox\/([0-9.]+)/);
            browserVersion = version ? `Firefox ${version[1]}` : browserVersion;
        } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
            const version = userAgent.match(/Version\/([0-9.]+)/);
            browserVersion = version ? `Safari ${version[1]}` : browserVersion;
        }

        return browserVersion;
    }
}

export interface Device {
    deviceNo: string;
    deviceType: DeviceType;
    registrationId: string;
    guiVersion: string;
    isIosSandBox: boolean;
}

export interface LoginResponse {
    id: string;
    userName: string;
    email: string;
    roles: string[];
    accessToken: string;
    refreshToken: string;
    hasCompletedPersonalizationSurvey?: boolean;
    verifiedEmailDate?: string | null;
    emailVerificationCooldownUntil?: string | null;
}

export interface CreateUserRequest {
    fullName: string;
    companyName: string;
    businessRegistrationNumber: string;
    tinNumber: string;
    email: string;
    addressLine1: string;
    addressLine2: string;
    postcode: string;
    country: number;
    phoneNumber: string;
    couponCode: string;
    isTncAgreed: boolean;
    agentCode: string;
    reCaptchaToken: string;
    planInformationId: number;
}

export interface CreateUserResponse {
    amount: number;
    userId: string;
    refNo: string;
    paymentType: PaymentType;
    orderId: number;
    orderType: OrderType;
    signature: string;
    merchantCode: string;
    responseUrl: string;
    callbackUrl: string;
}

export interface StripeCallbackRequest {
    respCode: string;
    respMessage: string;
    api: string;
    merchantNo: string;
    orderId: number;
    orderType: OrderType;
    trxnAmount: number;
    pymtMethod: string;
    trxnId: number;
    trxnDate: string;
    bankAccName: string;
    bankBranch: string;
    maskedCardNo: string;
    statusCode: string;
    statusDesc: string;
    signature: string;
}

export interface StripeCardDetailsSessionInterface {
    checkoutSessionId: string;
    clientSecret: string;
}

export interface EmailVerificationRequest {
    email: string;
    device: Device;
}

export interface PackageResponse {
    id: string;
    name: string;
    title: string;
    detail: string;
    price: number;
    validity: number;
}

export interface ResendEmailResponse {
    success: boolean;
    message: string;
    cooldownDurationSeconds?: number;
    cooldownUntil?: string; // ISO 8601 DateTime string
}
